import React, { useState } from 'react';
import ActiveFiltersIndicator from './ActiveFiltersIndicator';
import LayoutsDropdown from './LayoutsDropdown';
import './AppToolbar.scss';
import styleConstants from './variables.module.scss';
import AppSettingsModal from 'appToolbar/AppSettingsModal';
import classNames from 'classnames';
import {
    useDispatchHideAppToolbar,
    useDispatchShowAppToolbar,
} from 'app/store/appSettings/dispatcher';
import { useSelectIsAppToolbarHidden } from 'app/store/appSettings/selectors';
import { useSelectAlwaysShowMenuBar } from 'app/store/appSettings/selectors';
import PositionEntry from 'positionEntry/PositionEntry';
import { useMediaQuery } from 'react-responsive';
import useOnclickOutside from 'react-cool-onclickoutside';
import ContactSupportModal from 'app/contactSupport/ContactSupportModal';
import LockLayoutToggle from 'layout/LockLayoutToggle/LockLayoutToggle';

import {
    AppToolbar as CoreAppToolbar,
    Icon,
    ToolbarIcon,
} from '@instinet-analytics/semantic-ui-components';
import appLogo from './indigo-logo.svg';
import { AddWidgetModal } from 'appToolbar/AddWidgetModal/AddWidgetModal';
import { LayoutSaveButton } from 'appToolbar/LayoutSaveButton';
import { useNetwork } from 'utils/hooks/useNetwork';
import { PubSub } from '@instinet-analytics/core-lib';
import { OutdatedIndigoVersion } from 'app/OutdatedIndigoVersion/OutdatedIndigoVersion';
import { OpenFinWindowButtons } from 'app/openFin/OpenFinWindowButtons';

export default function AppToolbar(props: {
    layoutHeight: number;
    layoutWidth: number;
    onOpenAddWidgetModalPubSub: PubSub<void>;
}) {
    const alwaysShowMenuBar = useSelectAlwaysShowMenuBar();
    const isAppToolbarHidden = useSelectIsAppToolbarHidden();

    const dispatchHideAppToolbar = useDispatchHideAppToolbar();
    const dispatchShowAppToolbar = useDispatchShowAppToolbar();

    const online = useNetwork();

    const { layoutHeight, layoutWidth } = props;

    const collapse = () => dispatchHideAppToolbar();
    const show = () => dispatchShowAppToolbar();

    const [menuActive, setMenuState] = useState(false);
    const toggleMenu = () => setMenuState(!menuActive);

    const menuRef = useOnclickOutside(() => {
        setMenuState(false);
    });

    const isSmallWidth = useMediaQuery({
        query: `(max-width: ${styleConstants.toolbarWidthSmall})`,
    });
    const isMediumWidth = useMediaQuery({
        query: `(max-width: ${styleConstants.toolbarWidthMedium})`,
    });

    return (
        <CoreAppToolbar
            appLogoSrc={appLogo}
            collapsable={true}
            alwaysShow={alwaysShowMenuBar}
            isCollapsed={isAppToolbarHidden}
            startItems={
                <>
                    <PositionEntry />
                    {!online && (
                        <div className="network-disconnected">
                            <ToolbarIcon
                                name={'cloud'}
                                className="network-disconnected"
                                tooltip="Network disconnected"
                            />
                        </div>
                    )}
                    <OutdatedIndigoVersion iconView={isSmallWidth} />
                </>
            }
            handleShow={show}
            handleCollapse={collapse}
            endItems={
                <>
                    <LockLayoutToggle />
                    <div className="separator" />
                    <ActiveFiltersIndicator
                        suffix={<div className="separator active-filters" />}
                    />
                    <LayoutSaveButton className="narrow" />
                    <div ref={menuRef} className="last-section">
                        <div
                            className={classNames(
                                'menu-items',
                                menuActive && 'side-menu'
                            )}
                        >
                            <AddWidgetModal
                                onOpenAddWidgetModalPubSub={
                                    props.onOpenAddWidgetModalPubSub
                                }
                            />
                            <LayoutsDropdown
                                layoutHeight={layoutHeight}
                                layoutWidth={layoutWidth}
                            />
                            <LayoutSaveButton className="full-width" />
                            <div className="separator" />
                            <AppSettingsModal />
                            <ContactSupportModal />
                        </div>
                        {isMediumWidth && (
                            <div
                                className={classNames(
                                    'menu-button',
                                    menuActive && 'active'
                                )}
                            >
                                <Icon name="bars" onClick={toggleMenu} />
                            </div>
                        )}
                        <OpenFinWindowButtons />
                    </div>
                </>
            }
        />
    );
}
